import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getCharacter, ensureTalkingPhoto, type DbCharacter } from '@/api/astraea';
import { spendCredits, spendCreditsUnits, getCreditCosts } from '@/api/auth';
import {
  ensureIdentityProfile,
  ensureVoiceProfile,
  getAuthorizationProfile,
  upsertAuthorizationProfile,
  getMaterialAssets,
  getLeadHooks,
  getReplyMaterials,
  ensureDailyMaterialPack,
  generateMaterialPhoto,
  generateMaterialSilentVideo,
  generateMaterialTalkingVideo,
  generateLeadHook,
  generateSelectedDailyMaterials,
  confirmMaterialAsset,
  rejectMaterialAsset,
  recordMaterialUsage,
  incrementReplyUsage,
  updateBodyLock,
  getMaterialUsageHistory,
  generateMaterialText,
  type MaterialAsset,
  type LeadHookAsset,
  type ReplyMaterial,
  type IdentityProfile,
  type VoiceProfileRow,
  type DailyMaterialPack,
  type MaterialUsageRow,
  type TextMaterialType,
} from '@/api/materialLibrary';
import {
  buildCommercialPipeline,
  buildMaterialSuggestRows,
  type MaterialSuggestRow,
} from '@/api/commercialPipeline';
import type { TodayDecision, ConversionPlaybook } from '@/lib/commercial';
import TodayBriefingCard from '@/components/TodayBriefingCard';
import ConversionPlaybookCard from '@/components/ConversionPlaybookCard';
import { useAskConfirm, usePaidGenerationGuard } from '@/hooks/usePaidGenerationGuard';
import { PAID_GEN_ABANDON_MESSAGE, PAID_GEN_ABANDON_TITLE } from '@/lib/paidGeneration';
import {
  SCENE_LABELS,
  PHOTO_MATERIAL_CARDS,
  SILENT_VIDEO_CARDS,
  TALKING_VIDEO_CARD,
  EFFECT_FEEDBACK_OPTIONS,
  PLATFORM_OPTIONS,
  TEXT_MATERIAL_CARDS,
  RESULT_LABELS,
  USAGE_PURPOSE_LABELS,
  MATERIAL_TYPE_LABELS,
  DEFAULT_TALKING_SCRIPT,
  type SceneType,
  type PhotoSubtype,
  type SilentMotion,
} from '@/lib/materialScenes';
import { groupMaterialAssets } from '@/lib/member/assetGroups';

type TabId = 'pack' | 'photo' | 'silent' | 'talking' | 'text' | 'list' | 'hooks' | 'reply' | 'usage';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'pack', label: '今日素材包', icon: 'ri-gift-line' },
  { id: 'photo', label: '生成照片', icon: 'ri-camera-line' },
  { id: 'silent', label: '无声影片', icon: 'ri-film-line' },
  { id: 'talking', label: '口播影片', icon: 'ri-user-voice-line' },
  { id: 'text', label: '破冰文案', icon: 'ri-quill-pen-line' },
  { id: 'list', label: '素材列表', icon: 'ri-gallery-line' },
  { id: 'hooks', label: '引流钩子', icon: 'ri-hook-line' },
  { id: 'reply', label: '回复同步', icon: 'ri-chat-smile-2-line' },
  { id: 'usage', label: '效果回填', icon: 'ri-bar-chart-line' },
];

function copyText(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

async function downloadUrl(name: string, url: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = name;
    a.click();
    URL.revokeObjectURL(u);
  } catch {
    window.open(url, '_blank');
  }
}

export default function MaterialsPage() {
  const { characterId } = useParams();
  const cid = Number(characterId);
  const { askConfirm, confirmDialog } = useAskConfirm();
  const [tab, setTab] = useState<TabId>('pack');
  const [ch, setCh] = useState<DbCharacter | null>(null);
  const [identity, setIdentity] = useState<IdentityProfile | null>(null);
  const [voice, setVoice] = useState<VoiceProfileRow | null>(null);
  const [auth, setAuth] = useState({ face: false, body: false, voice: false });
  const [pack, setPack] = useState<DailyMaterialPack | null>(null);
  const [assets, setAssets] = useState<MaterialAsset[]>([]);
  const [hooks, setHooks] = useState<LeadHookAsset[]>([]);
  const [replies, setReplies] = useState<ReplyMaterial[]>([]);
  const [usageHistory, setUsageHistory] = useState<MaterialUsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [talkScript, setTalkScript] = useState(DEFAULT_TALKING_SCRIPT);
  const [feedbackPlatform, setFeedbackPlatform] = useState('dm');
  const [showIdentityDetail, setShowIdentityDetail] = useState(false);
  const [bodyTypeInput, setBodyTypeInput] = useState('');
  const [showBodyForm, setShowBodyForm] = useState(false);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [todayDecision, setTodayDecision] = useState<TodayDecision | null>(null);
  const [playbook, setPlaybook] = useState<ConversionPlaybook | null>(null);
  const [suggestRows, setSuggestRows] = useState<MaterialSuggestRow[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [jobProgress, setJobProgress] = useState<Record<string, 'pending' | 'running' | 'done' | 'fail'>>({});
  const [packStartedAt, setPackStartedAt] = useState<number | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  const hasPendingReview = assets.some((a) => a.status === 'pending_review');
  const hasGenerating = assets.some((a) => a.status === 'generating');
  const assetGroups = useMemo(() => groupMaterialAssets<MaterialAsset>(assets), [assets]);

  usePaidGenerationGuard({
    isGenerating: !!busy || hasGenerating,
    hasPendingResult: hasPendingReview,
    label: '角色素材',
    askConfirm,
  });

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const [character, idProf, vProf, authProf, dailyPack, mats, hks, rps, usage, costs] = await Promise.all([
        getCharacter(cid),
        ensureIdentityProfile(cid),
        ensureVoiceProfile(cid),
        getAuthorizationProfile(cid),
        ensureDailyMaterialPack(cid),
        getMaterialAssets(cid),
        getLeadHooks(cid),
        getReplyMaterials(cid),
        getMaterialUsageHistory(cid),
        getCreditCosts(),
      ]);
      setCh(character);
      setIdentity(idProf);
      setVoice(vProf);
      setAuth({
        face: authProf?.face_authorized ?? false,
        body: authProf?.body_authorized ?? false,
        voice: authProf?.voice_authorized ?? false,
      });
      setPack(dailyPack);
      setAssets(mats);
      setHooks(hks);
      setReplies(rps);
      setUsageHistory(usage);
      setBodyTypeInput((idProf?.body_lock as { body_type?: string })?.body_type || '');

      try {
        const pipeline = await buildCommercialPipeline(cid, {
          name: character?.name,
          description: character?.description || undefined,
        });
        setTodayDecision(pipeline.decision);
        setPlaybook(pipeline.playbook);
        const costMap = Object.fromEntries(costs.map((c) => [c.action, c.cost]));
        const rows = buildMaterialSuggestRows(pipeline.materialPlan, costMap);
        setSuggestRows(rows);
        setSelectedKeys((prev) => {
          if (prev.size > 0) return prev;
          return new Set(rows.map((r) => r.key));
        });
      } catch {
        setTodayDecision(null);
        setPlaybook(null);
        setSuggestRows([]);
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!hasGenerating && busy !== 'pack') return;
    const t = setInterval(() => { load(); }, 15000);
    return () => clearInterval(t);
  }, [hasGenerating, busy, load]);

  useEffect(() => {
    if (!packStartedAt || busy !== 'pack') return;
    const t = setInterval(() => setElapsedSec(Math.floor((Date.now() - packStartedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [packStartedAt, busy]);

  const recordFeedback = async (
    sourceType: 'asset' | 'hook' | 'reply',
    sourceId: number,
    result: string,
  ) => {
    await recordMaterialUsage({
      character_id: cid,
      source_type: sourceType,
      source_id: sourceId,
      platform: feedbackPlatform,
      result,
      note: feedbackNote || undefined,
    });
    setMsg(`已记录：${RESULT_LABELS[result] || result}`);
    await load();
  };

  const charge = async (action: string, units = 1) => {
    try {
      if (units > 1) await spendCreditsUnits(action, units, cid);
      else await spendCredits(action, cid);
      return true;
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '点数不足，请联络管理员加值');
      return false;
    }
  };

  const saveAuth = async (patch: Partial<{ face: boolean; body: boolean; voice: boolean }>) => {
    const next = { ...auth, ...patch };
    setAuth(next);
    await upsertAuthorizationProfile(cid, {
      face_authorized: next.face,
      body_authorized: next.body,
      voice_authorized: next.voice,
    });
  };

  const handleRejectAsset = async (assetId: number) => {
    const ok = await askConfirm(PAID_GEN_ABANDON_TITLE, PAID_GEN_ABANDON_MESSAGE, { confirmLabel: '确定放弃', cancelLabel: '再看看' });
    if (!ok) return;
    await rejectMaterialAsset(assetId);
    await load();
  };

  const onPhotoCard = async (scene: SceneType, subtype: PhotoSubtype, count: number) => {
    if (!auth.face) { setMsg('请先勾选「肖像授权」'); return; }
    if (!(await charge('gen_material_photo', count))) return;
    setBusy(`photo-${subtype}`);
    setMsg(null);
    try {
      for (let i = 0; i < count; i++) {
        await generateMaterialPhoto({ character_id: cid, scene_type: scene, photo_subtype: subtype, usage: 'moments' });
      }
      setMsg(`已生成 ${count} 张，请在素材列表确认入库`);
      setTab('list');
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '生成失败');
    } finally {
      setBusy(null);
    }
  };

  const onSilentCard = async (scene: SceneType, motion: SilentMotion) => {
    if (!auth.face) { setMsg('请先勾选「肖像授权」'); return; }
    if (!(await charge('gen_material_silent'))) return;
    setBusy(`silent-${motion}`);
    try {
      const r = await generateMaterialSilentVideo({ character_id: cid, scene_type: scene, motion, usage: 'dm_icebreaker' });
      setMsg(r.message || '无声影片生成中，约 2-5 分钟后刷新');
      setTab('list');
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '生成失败');
    } finally {
      setBusy(null);
    }
  };

  const onTalking = async () => {
    if (!auth.face || !auth.voice) { setMsg('请勾选肖像与声音授权，并绑定声音'); return; }
    if (!(await charge('gen_material_talking'))) return;
    setBusy('talking');
    try {
      if (!ch?.talking_photo_id) {
        setMsg('正在建立数字人脸…');
        await ensureTalkingPhoto(cid);
        await load();
      }
      const r = await generateMaterialTalkingVideo({ character_id: cid, script: talkScript });
      setMsg(r.message || (r.started ? '口播影片生成中(约1-3分钟)' : '已提交'));
      setTab('list');
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '生成失败');
    } finally {
      setBusy(null);
    }
  };

  const selectedRows = useMemo(
    () => suggestRows.filter((r) => selectedKeys.has(r.key)),
    [suggestRows, selectedKeys],
  );
  const totalPoints = useMemo(
    () => selectedRows.reduce((s, r) => s + r.points, 0),
    [selectedRows],
  );

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onConfirmSelectedGenerate = async () => {
    if (!auth.face) { setMsg('请先勾选「肖像授权」'); return; }
    if (selectedRows.length === 0) { setMsg('请至少勾选一项素材'); return; }

    const summary = selectedRows
      .map((r) => `· ${r.label}（${r.points} 点 · ${r.eta}）`)
      .join('\n');
    const ok = await askConfirm(
      '确认生成今日素材',
      `将扣 ${totalPoints} 点，开始生成：\n${summary}\n\n可以离开此页，完成后回到这里查看。`,
      { confirmLabel: `确认并扣 ${totalPoints} 点`, cancelLabel: '再看看' },
    );
    if (!ok) return;

    // 按勾选项分别扣点（单价仍由后端 credit_costs 决定）
    for (const row of selectedRows) {
      const charged = row.units > 1
        ? await charge(row.action, row.units)
        : await charge(row.action);
      if (!charged) return;
    }

    setBusy('pack');
    setPackStartedAt(Date.now());
    setElapsedSec(0);
    setJobProgress(Object.fromEntries(selectedRows.map((r) => [r.key, 'pending' as const])));
    setMsg('AI 正在生成你勾选的素材…');
    try {
      const r = await generateSelectedDailyMaterials(cid, [...selectedKeys], (key, phase) => {
        setJobProgress((prev) => ({
          ...prev,
          [key]: phase === 'start' ? 'running' : phase === 'done' ? 'done' : 'fail',
        }));
      });
      setMsg(r.message || '生成完成，请在下方查看成果');
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '生成失败');
    } finally {
      setBusy(null);
    }
  };

  const onHook = async (assetId?: number) => {
    if (!(await charge('gen_lead_hook'))) return;
    setBusy(assetId ? `hook-${assetId}` : 'hook');
    try {
      await generateLeadHook(cid, assetId, { usage: 'dm_icebreaker' });
      setMsg('引流钩子已生成，并同步到回复素材');
      setTab('hooks');
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '生成失败');
    } finally {
      setBusy(null);
    }
  };

  const onText = async (textType: TextMaterialType, count: number) => {
    if (!(await charge('gen_material_text', count))) return;
    setBusy(`text-${textType}`);
    try {
      const r = await generateMaterialText(cid, textType, count);
      setMsg(r.message || '文案已生成，请到「回复同步」查看');
      setTab('reply');
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '生成失败');
    } finally {
      setBusy(null);
    }
  };

  const saveBodyLock = async () => {
    if (!bodyTypeInput.trim()) return;
    await updateBodyLock(cid, bodyTypeInput.trim());
    setShowBodyForm(false);
    setMsg('身材设定已更新');
    await load();
  };

  const regenAsset = async (a: MaterialAsset) => {
    const mtype = a.material_type || a.asset_type;
    if (mtype === 'photo') {
      await onPhotoCard(
        (a.scene_type as SceneType) || 'friend_candid',
        (a.photo_subtype as PhotoSubtype) || 'friend_angle',
        1,
      );
    } else if (mtype === 'silent_video') {
      await onSilentCard(
        (a.scene_type as SceneType) || 'friend_candid_video',
        (a.motion as SilentMotion) || 'walk',
      );
    } else if (mtype === 'talking_video') {
      setTalkScript(DEFAULT_TALKING_SCRIPT);
      setTab('talking');
    }
  };

  const assetStatusLabel = (s: string) => {
    if (s === 'pending_review') return '待确认';
    if (s === 'generating') return '生成中';
    if (s === 'ready' || s === 'confirmed') return '已生成';
    return s;
  };

  if (!cid || loading) {
    return <div className="max-w-4xl mx-auto px-4 py-16 text-center text-secondary-400">加载中…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:py-10 pb-24">
      <div className="flex flex-wrap items-center gap-2 text-xs text-secondary-500">
        <Link to="/">首页</Link><span>/</span>
        <Link to="/materials">素材库</Link><span>/</span>
        <span className="text-foreground-800">{ch?.name}</span>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4 mt-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground-950">角色素材库</h1>
          <p className="text-sm text-foreground-600 mt-1 max-w-xl">
            固定角色长相、身材、声音与生活风格，生成可直接用于朋友圈、短视频、私信破冰与深度引流的素材。
          </p>
        </div>
        <Link to={`/board/${cid}`} className="text-xs text-secondary-500 border border-secondary-200 rounded-lg px-3 py-2 hover:border-primary-300">
          <i className="ri-settings-3-line mr-1"></i>策略任务板
        </Link>
      </div>

      {msg && <div className="mt-4 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2">{msg}</div>}

      {/* 身份锁定 */}
      <section className="mt-6 rounded-xl border border-secondary-200 bg-background-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-foreground-900"><i className="ri-fingerprint-line text-primary-500 mr-1"></i>角色身份锁定</h2>
          <span className="text-[10px] text-secondary-400">{identity?.identity_profile_id || `identity_${cid}`}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3 text-xs text-foreground-700">
          <div className="flex gap-3">
            {ch?.avatar_url && <img src={ch.avatar_url} alt="" className="w-16 h-20 object-cover rounded-lg" />}
            <div className="space-y-1">
              <div>五官：{(identity?.face_lock as { enabled?: boolean })?.enabled !== false ? '已锁定' : '待补充'}</div>
              <div>脸型：{(identity?.face_lock as { face_shape?: string })?.face_shape || '已绑定形象'}</div>
              <div>身材比例：{(identity?.body_lock as { body_ratio?: string })?.body_ratio || '自然比例'}</div>
              <div>身材：{(identity?.body_lock as { body_type?: string })?.body_type || '待补充'}</div>
              <div>穿搭风格：{(identity?.visual_style as { overall_style?: string })?.overall_style || '日常、普通真人感'}</div>
            </div>
          </div>
          <div className="space-y-1">
            <div>声音：{voice?.voice_status === 'ready' ? '已绑定' : '未绑定'} <span className="text-secondary-400">({voice?.voice_profile_id || `voice_${cid}`})</span></div>
            <div>地方语气：{(voice?.voice_profile as { accent?: string })?.accent || '普通话，可带轻微地方口音'}</div>
            <div>锁定级别：{identity?.identity_lock_level || 'face_body_voice'}</div>
            <div>授权状态：{auth.face && auth.body && auth.voice ? '已确认' : '待确认'}</div>
          </div>
        </div>
        {showIdentityDetail && (
          <div className="mt-3 p-3 rounded-lg bg-secondary-50 text-[11px] text-secondary-600 space-y-1">
            <div>一致性规则：{(identity?.face_lock as { consistency_rule?: string })?.consistency_rule || '—'}</div>
            <div>拍摄风格：{(identity?.visual_style as { camera_style?: string })?.camera_style || '手机拍摄、自然'}</div>
            <div>避免风格：{((identity?.visual_style as { avoid_style?: string[] })?.avoid_style || []).join('、') || '网红大片、商业广告'}</div>
          </div>
        )}
        {showBodyForm && (
          <div className="mt-3 flex flex-wrap gap-2 items-end">
            <label className="text-xs flex-1 min-w-[200px]">
              身材描述
              <input value={bodyTypeInput} onChange={(e) => setBodyTypeInput(e.target.value)} placeholder="普通女生身材，偏自然，不夸张" className="mt-1 w-full rounded-lg border border-secondary-200 px-2 py-1.5 text-sm" />
            </label>
            <button type="button" onClick={saveBodyLock} className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs cursor-pointer">保存</button>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" onClick={() => setShowIdentityDetail((v) => !v)} className="px-2.5 py-1 rounded-lg border border-secondary-200 text-xs cursor-pointer">查看身份设定</button>
          <button type="button" onClick={() => setShowBodyForm((v) => !v)} className="px-2.5 py-1 rounded-lg border border-secondary-200 text-xs cursor-pointer">补充身材设定</button>
          <Link to={`/board/${cid}`} className="px-2.5 py-1 rounded-lg border border-secondary-200 text-xs">更换参考照片</Link>
          <Link to={`/board/${cid}`} className="px-2.5 py-1 rounded-lg border border-secondary-200 text-xs">更换声音</Link>
        </div>
        <div className="flex flex-wrap gap-4 mt-4 text-xs">
          {(['face', 'body', 'voice'] as const).map((k) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={auth[k]} onChange={(e) => saveAuth({ [k]: e.target.checked })} />
              {k === 'face' ? '肖像授权' : k === 'body' ? '形象授权' : '声音授权'}
            </label>
          ))}
        </div>
      </section>

      {/* Tab 导航 */}
      <div className="flex flex-wrap gap-1 mt-6 border-b border-secondary-200 pb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer ${tab === t.id ? 'bg-primary-100 text-primary-700' : 'text-secondary-600 hover:bg-secondary-50'}`}
          >
            <i className={`${t.icon} mr-1`}></i>{t.label}
          </button>
        ))}
      </div>

      {/* 今日主流程：分析 → 建议 → 勾选 → 确认点数 → 进度 → 同页成果 → SOP */}
      {tab === 'pack' && (
        <section className="mt-4 space-y-4">
          <div className="rounded-xl border border-primary-200 bg-primary-50/40 p-4">
            <h2 className="text-sm font-semibold text-foreground-900">今天要做的事</h2>
            <p className="text-xs text-secondary-600 mt-1">
              AI 已读角色设定与近期素材，帮你排好今日建议。勾选后确认点数，再开始生成。
            </p>

            {todayDecision?.explanation && (
              <TodayBriefingCard explanation={todayDecision.explanation} className="mt-3" />
            )}

            <div className="mt-4">
              <div className="text-xs font-semibold text-foreground-900 mb-2">今日建议素材（自行勾选）</div>
              {suggestRows.length === 0 ? (
                <p className="text-xs text-secondary-500">今日建议加载中或暂无建议，可先到下方自定义生成。</p>
              ) : (
                <ul className="space-y-2">
                  {suggestRows.map((row) => {
                    const prog = jobProgress[row.key];
                    return (
                      <li key={row.key} className="rounded-lg border border-secondary-200 bg-white px-3 py-2.5">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={selectedKeys.has(row.key)}
                            disabled={busy === 'pack'}
                            onChange={() => toggleKey(row.key)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground-900">{row.label}</div>
                            <div className="text-[11px] text-secondary-600 mt-0.5">用途：{USAGE_PURPOSE_LABELS[row.usage] || row.usage || '经营素材'}</div>
                            {row.reason && <div className="text-[11px] text-secondary-500 mt-0.5">原因：{row.reason}</div>}
                            <div className="text-[11px] text-primary-700 mt-1">
                              {row.points} 点 · {row.eta}
                              {prog === 'running' && <span className="ml-2 text-amber-600">生成中…</span>}
                              {prog === 'done' && <span className="ml-2 text-green-600">已提交</span>}
                              {prog === 'fail' && <span className="ml-2 text-red-600">失败</span>}
                            </div>
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-800">
                <span>已选 {selectedRows.length} 项</span>
                <span className="font-semibold text-primary-700">合计 {totalPoints} 点</span>
                {busy === 'pack' && (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <span className="inline-block w-3 h-3 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" />
                    已进行 {elapsedSec}s · 可离开，完成后回到本页
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  type="button"
                  disabled={!!busy || selectedRows.length === 0}
                  onClick={onConfirmSelectedGenerate}
                  className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-semibold disabled:opacity-50 cursor-pointer"
                >
                  {busy === 'pack' ? '生成中…' : `确认并生成（${totalPoints} 点）`}
                </button>
                <button type="button" onClick={() => setTab('list')} className="px-4 py-2 rounded-lg border border-secondary-300 bg-white text-sm cursor-pointer">
                  查看素材列表
                </button>
                <button type="button" onClick={() => setTab('photo')} className="px-4 py-2 rounded-lg border border-secondary-300 bg-white text-sm cursor-pointer">
                  自定义单项生成
                </button>
              </div>
            </div>
          </div>

          {/* 同页成果 */}
          <div className="rounded-xl border border-secondary-200 bg-background-50 p-4">
            <h3 className="text-sm font-semibold text-foreground-900">今日成果（同页可下载）</h3>
            <p className="text-[11px] text-secondary-500 mt-1">生成完成后直接在这里预览与下载，不必跳转。</p>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {assets.slice(0, 8).map((a) => (
                <div key={a.id} className="rounded-lg border border-secondary-100 p-2">
                  {a.asset_type === 'image' && a.url && (
                    <img src={a.url} alt="" className="w-full h-36 object-cover rounded" />
                  )}
                  {a.asset_type === 'video' && a.url && (
                    <video src={a.url} controls className="w-full h-36 object-cover rounded bg-black" />
                  )}
                  {!a.url && <div className="h-36 flex items-center justify-center text-xs text-secondary-400 bg-secondary-50 rounded">{assetStatusLabel(a.status)}</div>}
                  <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]">
                    <span className="text-secondary-600 truncate">{MATERIAL_TYPE_LABELS[(a.material_type as keyof typeof MATERIAL_TYPE_LABELS) || 'photo'] || a.material_type || a.asset_type}</span>
                    {a.url && (
                      <button type="button" className="text-primary-600 cursor-pointer" onClick={() => downloadUrl(`material-${a.id}`, a.url!)}>下载</button>
                    )}
                  </div>
                </div>
              ))}
              {assets.length === 0 && <p className="text-xs text-secondary-500 col-span-2">还没有素材成果。</p>}
            </div>
            {replies.length > 0 && (
              <div className="mt-4 border-t border-secondary-100 pt-3">
                <div className="text-xs font-semibold text-foreground-900 mb-2">话术成果</div>
                <ul className="space-y-2">
                  {replies.slice(0, 5).map((r) => (
                    <li key={r.id} className="text-xs text-foreground-800 bg-white border border-secondary-100 rounded-lg px-3 py-2 flex gap-2">
                      <span className="flex-1 whitespace-pre-wrap">{r.content}</span>
                      {r.content && (
                        <button type="button" className="text-primary-600 flex-shrink-0 cursor-pointer" onClick={() => copyText(r.content!)}>复制</button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {playbook && <ConversionPlaybookCard playbook={playbook} />}
        </section>
      )}

      {/* 照片类型卡片 */}
      {tab === 'photo' && (
        <section className="mt-4 grid sm:grid-cols-2 gap-3">
          {PHOTO_MATERIAL_CARDS.map((card) => (
            <div key={`${card.scene}-${card.id}`} className="rounded-xl border border-secondary-200 bg-background-50 p-4">
              <div className="text-sm font-semibold text-foreground-900">{card.title}</div>
              <p className="text-xs text-secondary-600 mt-1">{card.description}</p>
              <p className="text-[11px] text-secondary-500 mt-2">适合：{card.purposes.join(' / ')}</p>
              <p className="text-[11px] text-secondary-400">对象：{card.targetUser}</p>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => onPhotoCard(card.scene, card.id, card.defaultCount)}
                className="mt-3 px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-semibold disabled:opacity-50 cursor-pointer"
              >
                生成 {card.defaultCount} 张
              </button>
            </div>
          ))}
        </section>
      )}

      {/* 无声影片 */}
      {tab === 'silent' && (
        <section className="mt-4 grid sm:grid-cols-2 gap-3">
          {SILENT_VIDEO_CARDS.map((card) => (
            <div key={`${card.scene}-${card.motion}-${card.title}`} className="rounded-xl border border-secondary-200 bg-background-50 p-4">
              <div className="text-sm font-semibold text-foreground-900">{card.title}</div>
              <p className="text-xs text-secondary-600 mt-1">{card.description}</p>
              <p className="text-[11px] text-secondary-500 mt-2">适合：{card.purposes.join(' / ')}</p>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => onSilentCard(card.scene, card.motion)}
                className="mt-3 px-3 py-1.5 rounded-lg border border-primary-300 text-primary-700 text-xs font-semibold disabled:opacity-50 cursor-pointer"
              >
                生成无声影片
              </button>
            </div>
          ))}
        </section>
      )}

      {/* 口播影片 */}
      {tab === 'talking' && (
        <section className="mt-4 rounded-xl border border-secondary-200 bg-background-50 p-4">
          <div className="text-sm font-semibold">{TALKING_VIDEO_CARD.title}</div>
          <p className="text-xs text-secondary-600 mt-1">{TALKING_VIDEO_CARD.description}</p>
          <p className="text-[11px] text-secondary-500 mt-2">适合：{TALKING_VIDEO_CARD.purposes.join(' / ')} · {TALKING_VIDEO_CARD.duration}</p>
          <label className="block mt-4 text-xs text-secondary-500">
            口播脚本（像普通人分享，不要导师腔）
            <textarea
              value={talkScript}
              onChange={(e) => setTalkScript(e.target.value)}
              rows={4}
              className="mt-1 w-full rounded-lg border border-secondary-200 px-3 py-2 text-sm"
            />
          </label>
          <button type="button" disabled={!!busy} onClick={onTalking} className="mt-3 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-semibold disabled:opacity-50 cursor-pointer">
            {busy === 'talking' ? '提交中…' : '生成口播影片'}
          </button>
          <p className="text-[11px] text-amber-700 mt-2">口播按 HeyGen 时长计费，生成前请确认已绑定数字人脸与声音。</p>
        </section>
      )}

      {/* 破冰文案 */}
      {tab === 'text' && (
        <section className="mt-4 grid sm:grid-cols-2 gap-3">
          {TEXT_MATERIAL_CARDS.map((card) => (
            <div key={card.type} className="rounded-xl border border-secondary-200 bg-background-50 p-4">
              <div className="text-sm font-semibold text-foreground-900">{card.title}</div>
              <p className="text-xs text-secondary-600 mt-1">{card.description}</p>
              <p className="text-[11px] text-secondary-500 mt-2">适合：{card.purposes.join(' / ')}</p>
              <p className="text-[11px] text-secondary-400">对象：{card.targetUser}</p>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => onText(card.type, card.defaultCount)}
                className="mt-3 px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-semibold disabled:opacity-50 cursor-pointer"
              >
                生成 {card.defaultCount} 条
              </button>
            </div>
          ))}
        </section>
      )}

      {/* 素材列表 */}
      {tab === 'list' && (
        <section className="mt-4">
          <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
            <span className="text-secondary-500">回填平台：</span>
            {PLATFORM_OPTIONS.map((p) => (
              <button key={p.value} type="button" onClick={() => setFeedbackPlatform(p.value)} className={`px-2 py-0.5 rounded cursor-pointer ${feedbackPlatform === p.value ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100'}`}>{p.label}</button>
            ))}
            <input value={feedbackNote} onChange={(e) => setFeedbackNote(e.target.value)} placeholder="备注(可选)" className="ml-auto flex-1 min-w-[120px] rounded border border-secondary-200 px-2 py-0.5 text-xs" />
          </div>
          {assets.length === 0 ? (
            <p className="text-xs text-secondary-500">还没有素材，从「今日素材包」或「生成照片」开始。</p>
          ) : (
            <div className="space-y-6">
              {assetGroups.map((group) => (
                <div key={group.key}>
                  <h3 className="text-xs font-semibold text-secondary-600 mb-2 flex items-center gap-2">
                    <span>{group.label}</span>
                    <span className="text-secondary-400 font-normal">({group.assets.length})</span>
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {group.assets.map((a) => {
                      const mtype = a.material_type || a.asset_type;
                      const usageLabel = USAGE_PURPOSE_LABELS[a.usage_purpose || ''] || a.usage_purpose || '—';
                      return (
                        <div key={a.id} className="rounded-xl border border-secondary-200 bg-background-50 overflow-hidden">
                          {a.asset_type === 'image' && a.url && <img src={a.url} alt="" className="w-full aspect-[3/4] object-cover" />}
                          {a.asset_type === 'video' && a.url && <video src={a.url} controls className="w-full aspect-[9/16] object-cover bg-black" />}
                          {a.status === 'generating' && <div className="aspect-video flex items-center justify-center text-xs text-secondary-400">生成中…</div>}
                          {a.status === 'pending_review' && a.url && (
                            <div className="p-2 bg-amber-50 text-xs text-amber-800 flex gap-2">
                              <button type="button" onClick={() => confirmMaterialAsset(a.id, cid).then(load)} className="underline cursor-pointer">确认入库</button>
                              <button type="button" onClick={() => handleRejectAsset(a.id)} className="underline cursor-pointer">放弃</button>
                            </div>
                          )}
                          <div className="p-3 text-xs space-y-1">
                            <div className="font-medium">{MATERIAL_TYPE_LABELS[mtype as keyof typeof MATERIAL_TYPE_LABELS] || String(mtype)}</div>
                            <div className="text-secondary-500">场景：{SCENE_LABELS[a.scene_type as SceneType] || String(a.scene_type || '—')}</div>
                            <div className="text-secondary-500">用途：{usageLabel}</div>
                            <div className="text-secondary-500">适合对象：有服务但主页说不清的人</div>
                            <div className="text-secondary-400">状态：{assetStatusLabel(a.status)}</div>
                            <div className="flex flex-wrap gap-2 pt-2">
                              {a.url && <button type="button" onClick={() => downloadUrl(`material-${a.id}`, a.url!)} className="text-primary-600 cursor-pointer">下载</button>}
                              <button type="button" disabled={!!busy} onClick={() => regenAsset(a)} className="text-primary-600 cursor-pointer">生成同款</button>
                              <button type="button" disabled={!!busy} onClick={() => onHook(a.id)} className="text-primary-600 cursor-pointer">生成引流钩子</button>
                              <button type="button" onClick={() => { setTab('reply'); setMsg('话术已在回复同步中'); }} className="text-primary-600 cursor-pointer">同步到回复工具</button>
                            </div>
                            <div className="flex flex-wrap gap-1 pt-2 border-t border-secondary-100">
                              {EFFECT_FEEDBACK_OPTIONS.map((f) => (
                                <button key={f.value} type="button" onClick={() => recordFeedback('asset', a.id, f.value)} className="px-1.5 py-0.5 rounded bg-secondary-100 hover:bg-secondary-200 cursor-pointer text-[10px]">{f.label}</button>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* 引流钩子 */}
      {tab === 'hooks' && (
        <section className="mt-4 space-y-3">
          <button type="button" disabled={!!busy} onClick={() => onHook()} className="text-xs text-primary-600 cursor-pointer mb-2">+ 生成新引流钩子</button>
          {hooks.length === 0 ? (
            <p className="text-xs text-secondary-500">生成素材后可一键配钩子。</p>
          ) : hooks.map((h) => (
            <div key={h.id} className="rounded-xl border border-secondary-200 bg-background-50 p-4 text-xs space-y-2">
              <div className="font-semibold text-sm">引流钩子：{(h.asset_content as { title?: string })?.title || '检查表'}</div>
              <div><span className="text-secondary-500">适合对象：</span>{h.target_user}</div>
              <div><span className="text-secondary-500">公开触发：</span>{h.public_trigger}</div>
              <div><span className="text-secondary-500">评论关键词：</span>{h.comment_keyword}</div>
              <div><span className="text-secondary-500">公开回复：</span>{h.public_reply}</div>
              <div><span className="text-secondary-500">私信开场：</span>{h.dm_opening}</div>
              <div><span className="text-secondary-500">追问：</span>{h.follow_up_question}</div>
              {(h.asset_content as { items?: string[] })?.items && (
                <div className="text-secondary-600">
                  <span className="text-secondary-500">承接资料：</span>
                  <ul className="list-disc ml-4 mt-1">{(h.asset_content as { items: string[] }).items.map((it) => <li key={it}>{it}</li>)}</ul>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                {h.public_reply && <button type="button" onClick={() => copyText(h.public_reply!)} className="text-primary-600 cursor-pointer">复制公开回复</button>}
                {h.dm_opening && <button type="button" onClick={() => copyText(h.dm_opening!)} className="text-primary-600 cursor-pointer">复制私信</button>}
                <button type="button" onClick={() => { setTab('reply'); setMsg('已同步到回复工具'); }} className="text-primary-600 cursor-pointer">同步到回复工具</button>
              </div>
              <div className="flex flex-wrap gap-1 pt-2 border-t border-secondary-100">
                {EFFECT_FEEDBACK_OPTIONS.map((f) => (
                  <button key={f.value} type="button" onClick={() => recordFeedback('hook', h.id, f.value)} className="px-2 py-0.5 rounded bg-secondary-100 hover:bg-secondary-200 cursor-pointer">{f.label}</button>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* 回复同步 */}
      {tab === 'reply' && (
        <section className="mt-4">
          <p className="text-xs text-secondary-500 mb-3">引流钩子生成后会自动同步到这里。也可在<Link to="/reply" className="text-primary-600 mx-1">回复工具</Link>直接使用。</p>
          {replies.length === 0 ? (
            <p className="text-xs text-secondary-500">还没有回复话术，先生成引流钩子。</p>
          ) : (
            <div className="space-y-2">
              {replies.map((r) => (
                <div key={r.id} className="rounded-lg border border-secondary-200 p-3 text-xs">
                  <div className="flex gap-2 mb-1">
                    <span className="text-secondary-400 w-16 flex-shrink-0">{r.scene}</span>
                    <span className="text-secondary-400">{r.reply_type}</span>
                    {r.target_user_type && <span className="text-secondary-400">· {r.target_user_type}</span>}
                  </div>
                  <p className="text-foreground-800">{r.content}</p>
                  {r.next_question && <p className="text-secondary-500 mt-1">下一句追问：{r.next_question}</p>}
                  {r.lead_asset && <p className="text-secondary-500">承接资料：{r.lead_asset}</p>}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button type="button" onClick={() => { copyText(r.content || ''); incrementReplyUsage(r.id); }} className="text-primary-600 cursor-pointer">复制</button>
                    <button type="button" onClick={() => incrementReplyUsage(r.id).then(() => setMsg('已标记使用'))} className="text-secondary-600 cursor-pointer">标记已使用</button>
                    <button type="button" onClick={() => recordFeedback('reply', r.id, 'replied')} className="text-green-600 cursor-pointer">有效</button>
                    <button type="button" onClick={() => recordFeedback('reply', r.id, 'rejected')} className="text-red-600 cursor-pointer">无效</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* 效果回填记录 */}
      {tab === 'usage' && (
        <section className="mt-4">
          <p className="text-xs text-secondary-500 mb-3">业务使用素材后的回填记录，用于下次生成更接近有效素材。</p>
          {usageHistory.length === 0 ? (
            <p className="text-xs text-secondary-500">还没有回填记录，使用素材后可在素材卡上点选结果。</p>
          ) : (
            <div className="space-y-2">
              {usageHistory.map((u) => (
                <div key={u.id} className="rounded-lg border border-secondary-200 p-3 text-xs">
                  <div className="flex justify-between gap-2 text-secondary-500">
                    <span>{u.source_type} #{u.source_id}</span>
                    <span>{new Date(u.created_at).toLocaleString('zh-CN')}</span>
                  </div>
                  <div className="mt-1">
                    <span className="text-foreground-800">{RESULT_LABELS[u.result || ''] || u.result}</span>
                    {u.platform && <span className="text-secondary-400 ml-2">· {PLATFORM_OPTIONS.find((p) => p.value === u.platform)?.label || u.platform}</span>}
                  </div>
                  {u.note && <p className="text-secondary-500 mt-1">备注：{u.note}</p>}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {confirmDialog}
    </div>
  );
}
